import styled from "styled-components";
import { theme } from "../../theme/semanticTheme";

const placeholderColor = "rgba(88, 94, 109, 0.5)";

export const FieldWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

export const InputShell = styled.div`
  position: relative;
  width: 100%;
`;

export const FloatingLabel = styled.label`
  position: absolute;
  left: ${theme.spacing.md};
  top: 0;
  transform: ${(p) => (p.$float ? "translateY(-50%) scale(0.92)" : "translateY(14px) scale(1)")};
  transform-origin: left top;
  font-size: ${theme.typography.inputLabel.fontSize};
  font-weight: ${theme.typography.inputLabel.fontWeight};
  line-height: ${theme.typography.inputLabel.lineHeight};
  color: ${(p) => {
    if (p.$error) return theme.colors.error;
    if (p.$focused) return theme.colors.purple;
    return theme.colors.textMuted;
  }};
  padding: 0 6px;
  background: ${theme.colors.background};
  pointer-events: none;
  transition: transform 0.15s ease, color 0.15s ease;
`;

export const InputBase = styled.input`
  width: 100%;
  padding: ${(p) => (p.$hasLabel ? `${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.sm}` : `${theme.spacing.sm} ${theme.spacing.md}`)};
  padding-left: ${(p) => (p.$hasStartAdornment ? "40px" : undefined)};
  padding-right: ${(p) => (p.$hasEndAdornment ? "44px" : undefined)};
  font-size: ${theme.typography.inputValue.fontSize};
  font-weight: ${theme.typography.inputValue.fontWeight};
  line-height: ${theme.typography.inputValue.lineHeight};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  background: ${theme.colors.background};
  border: 1px solid ${(p) => (p.$error ? theme.colors.error : theme.colors.border)};
  border-radius: ${theme.borderRadius.md};
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${placeholderColor};
  }

  &:focus {
    border-color: ${(p) => (p.$error ? theme.colors.error : theme.colors.purple)};
    box-shadow: 0 0 0 2px ${(p) => (p.$error ? "rgba(232, 44, 53, 0.15)" : "rgba(57, 0, 149, 0.12)")};
  }

  &:disabled,
  &[readonly] {
    background: ${theme.colors.backgroundAlt};
    color: ${theme.colors.textMuted};
    cursor: not-allowed;
  }

  /* Date input: empty state (dd/mm/yyyy) in light grey like placeholders */
  &[type="date"]::-webkit-datetime-edit,
  &[type="datetime-local"]::-webkit-datetime-edit {
    color: ${(p) => (p.$hasValue ? theme.colors.text : placeholderColor)};
  }
`;
export const StartAdornment = styled.div`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textMuted};
  pointer-events: none;
`;

export const EndAdornmentButton = styled.button`
  position: absolute;
  right: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  height: 32px;
  width: 32px;
  border: 0;
  padding: 0;
  border-radius: ${theme.borderRadius.sm};
  background: transparent;
  color: ${theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background: ${theme.colors.backgroundAlt};
    color: ${theme.colors.text};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const TextAreaBase = styled.textarea`
  width: 100%;
  padding: ${(p) => (p.$hasLabel ? `${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.sm}` : `${theme.spacing.sm} ${theme.spacing.md}`)};
  font-size: ${theme.typography.inputValue.fontSize};
  font-weight: ${theme.typography.inputValue.fontWeight};
  line-height: ${theme.typography.inputValue.lineHeight};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  background: ${theme.colors.background};
  border: 1px solid ${(p) => (p.$error ? theme.colors.error : theme.colors.border)};
  border-radius: ${theme.borderRadius.md};
  outline: none;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${placeholderColor};
  }

  &:focus {
    border-color: ${(p) => (p.$error ? theme.colors.error : theme.colors.purple)};
    box-shadow: 0 0 0 2px ${(p) => (p.$error ? "rgba(232, 44, 53, 0.15)" : "rgba(57, 0, 149, 0.12)")};
  }

  &:disabled,
  &[readonly] {
    background: ${theme.colors.backgroundAlt};
    color: ${theme.colors.textMuted};
    cursor: not-allowed;
  }
`;

export const SelectBase = styled.select`
  width: 100%;
  padding: ${(p) => (p.$hasLabel ? `${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.sm}` : `${theme.spacing.sm} ${theme.spacing.md}`)};
  font-size: ${theme.typography.inputValue.fontSize};
  font-weight: ${theme.typography.inputValue.fontWeight};
  line-height: ${theme.typography.inputValue.lineHeight};
  font-family: ${theme.typography.fontFamily};
  color: ${(p) => (p.$hasValue ? theme.colors.text : placeholderColor)};
  background: ${theme.colors.background};
  border: 1px solid ${(p) => (p.$error ? theme.colors.error : theme.colors.border)};
  border-radius: ${theme.borderRadius.md};
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23585e6d' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right ${theme.spacing.md} center;
  padding-right: 32px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: ${(p) => (p.$error ? theme.colors.error : theme.colors.purple)};
    box-shadow: 0 0 0 2px ${(p) => (p.$error ? "rgba(232, 44, 53, 0.15)" : "rgba(57, 0, 149, 0.12)")};
  }

  &:disabled {
    background-color: ${theme.colors.backgroundAlt};
    color: ${theme.colors.textMuted};
    cursor: not-allowed;
  }
`;

export const ErrorText = styled.span`
  font-size: ${theme.typography.caption.fontSize};
  font-weight: ${theme.typography.caption.fontWeight};
  line-height: ${theme.typography.caption.lineHeight};
  color: ${theme.colors.error};
`;

export const HelperText = styled.span`
  font-size: ${theme.typography.caption.fontSize};
  font-weight: ${theme.typography.caption.fontWeight};
  line-height: ${theme.typography.caption.lineHeight};
  color: ${theme.colors.textMuted};
`;
